import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Award, TrendingUp, Zap, Target } from 'lucide-react';

const Stats = () => {
  const { t } = useLanguage();

  const seasonStats = [
    { label: t.stats.matches, value: '22' },
    { label: t.stats.starts, value: '18' },
    { label: t.stats.minutes, value: '1.445' },
    { label: t.stats.goals, value: '5' },
    { label: t.stats.assists, value: '3' }
  ];

  const offensiveStats = [
    { label: t.stats.dribbles, value: t.stats.dribblesValue },
    { label: t.stats.keyPasses, value: t.stats.keyPassesValue },
    { label: t.stats.duels, value: t.stats.duelsValue }
  ];

  const physicalStats = [
    { label: t.stats.maxSpeed, value: t.stats.maxSpeedValue },
    { label: t.stats.distance, value: t.stats.distanceValue }
  ];

  const defensiveStats = [
    { label: t.stats.recoveries, value: t.stats.recoveriesValue }
  ];

  return (
    <section id="stats" className="py-24 bg-gradient-to-b from-black to-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl md:text-6xl font-bold mb-4 tracking-wide"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            {t.stats.title}
          </h2>
          <div className="h-1 w-20 bg-red-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t.stats.season}
          </p>
        </div>

        {/* Season Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-16">
          {seasonStats.map((stat, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:border-red-600/50 transition-all duration-300 hover:transform hover:-translate-y-1"
            >
              <p
                className="text-4xl md:text-5xl font-bold mb-2 text-red-600"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                {stat.value}
              </p>
              <p className="text-gray-400 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Detailed Stats Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Offensive Performance */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-600/10 p-3 rounded-xl">
                <Target className="text-red-600" size={28} />
              </div>
              <h3
                className="text-2xl font-bold"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {t.stats.offensive}
              </h3>
            </div>
            <div className="space-y-4">
              {offensiveStats.map((stat, index) => (
                <div key={index} className="border-b border-white/10 pb-3">
                  <p className="text-gray-400 text-sm mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.label}
                  </p>
                  <p className="text-white text-lg font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Physical Profile */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-600/10 p-3 rounded-xl">
                <Zap className="text-red-600" size={28} />
              </div>
              <h3
                className="text-2xl font-bold"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {t.stats.physical}
              </h3>
            </div>
            <div className="space-y-4">
              {physicalStats.map((stat, index) => (
                <div key={index} className="border-b border-white/10 pb-3">
                  <p className="text-gray-400 text-sm mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.label}
                  </p>
                  <p className="text-white text-lg font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Without Ball */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-600/10 p-3 rounded-xl">
                <TrendingUp className="text-red-600" size={28} />
              </div>
              <h3
                className="text-2xl font-bold"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {t.stats.defensive}
              </h3>
            </div>
            <div className="space-y-4">
              {defensiveStats.map((stat, index) => (
                <div key={index} className="border-b border-white/10 pb-3">
                  <p className="text-gray-400 text-sm mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.label}
                  </p>
                  <p className="text-white text-lg font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
